import React, { useState } from "react"

import ActivityCard from "~components/shared/activity-card/activity-card"
import ActivityCardModal from "~components/shared/activity-card/activity-card-modal"
import ActivityCardWrapper from "~components/shared/activity-card/activity-card-wrapper"
import type { CollectionType } from "~components/shared/collections/generic-collection"
import GenericCollection, {
    TYPES,
} from "~components/shared/collections/generic-collection"
import ViewObserverContainer from "~components/shared/todo-lib-react-components/view-observer-container"
import type {
    ActivityFieldsFragment,
    MediaFieldsFragment,
} from "~graphql/generated/graphql"
import { navigateToActivityModal } from "~utils/navigation-helpers"
import { useMobileMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import useSingleFireEventHandler from "~utils/use-single-fire-event-handler"
import Spinner from "~components/shared/todo-lib-react-components/spinner"

export interface ActivitiesCollectionProps {
    readonly activities: ActivityFieldsFragment[]
    readonly avatar?: MediaFieldsFragment | null
    readonly title: string
    readonly label?: string
    readonly type?: CollectionType
    readonly isLoading?: boolean
    readonly canFetchMore?: boolean
    readonly fetchMore: () => void
}

export default function ActivitiesCollection({
    activities,
    avatar,
    title,
    label,
    type = TYPES.LIST,
    isLoading,
    canFetchMore,
    fetchMore,
}: ActivitiesCollectionProps) {
    const [hasBeenScrolled, setHasBeenScrolled] = useState(false)
    const { isMobileOnly } = useMobileMediaQueries()
    const onActivityClick = (activity: ActivityFieldsFragment) => {
        void navigateToActivityModal(activity.id)
    }

    const handleScrollOrSwipe = useSingleFireEventHandler({
        handlerFunc: () => setHasBeenScrolled(true),
    })

    const shouldShowLoadingDots = (isLoading || canFetchMore) && !isMobileOnly

    return (
        <>
            <GenericCollection
                noPadding
                label={label}
                type={type}
                onScrollOrSwipe={handleScrollOrSwipe}
            >
                {activities.map((activity, index) => (
                    <ActivityCardWrapper
                        key={activity.id}
                        isMobile={isMobileOnly}
                        fetchMore={fetchMore}
                        canFetchMore={canFetchMore}
                        isLastComponent={index === activities.length - 1}
                    >
                        <ActivityCard
                            activity={activity}
                            avatar={avatar}
                            title={title}
                            onClick={onActivityClick}
                            isInteractedWithEventTrackingEnabled={
                                hasBeenScrolled
                            }
                        />
                    </ActivityCardWrapper>
                ))}
                {shouldShowLoadingDots && (
                    <ViewObserverContainer onViewed={fetchMore}>
                        <Spinner />
                    </ViewObserverContainer>
                )}
            </GenericCollection>
            <ActivityCardModal title={title} avatar={avatar} />
        </>
    )
}
